import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, BadgeCheck, MessageSquare, FolderOpen, Zap, Users, PlayCircle } from 'lucide-react';
import { ProductTour } from '@/components/ProductTour';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { Logo } from '@/components/Shell';
import { Button, useToast } from '@/components/ui';
import type { PublicConfig } from '@shared/types';

/**
 * Signs straight into the demo organisation.
 *
 * The credentials are public on purpose — the demo holds no real data and can
 * be rebuilt at any time. Making a visitor copy an email and a password out of
 * a paragraph before they can look round loses most of them; one button does
 * not. Hidden entirely when no demo has been set up, rather than offering a
 * button that fails.
 */
function DemoButton({ demo }: { demo: { email: string; password: string } | null }) {
  const { signInStaff } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (!demo?.email || !demo?.password) return null;

  async function open() {
    setBusy(true);
    try {
      await signInStaff(demo!.email, demo!.password);
      navigate('/app');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'The demo is unavailable just now');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant="outline"
      size="lg"
      onClick={() => void open()}
      loading={busy}
      icon={<PlayCircle className="h-4 w-4" />}
    >
      See a live demo
    </Button>
  );
}

/**
 * The currencies PROVIA is actually used in, in the hero.
 *
 * Not decoration. The product was written for one market and reads like it —
 * "shilling" in the headline told a landlord in Manchester or on the Gold
 * Coast that this was not for them, which is the opposite of true: the
 * jurisdiction table already knows about deposits in Britain and bonds in
 * Australia, and money is stored in minor units with the symbol picked per
 * organisation. So the line says all of them, one at a time.
 *
 * Kept to currencies 36_Jurisdictions.gs has a country hint for, because a
 * word here is a claim that somebody letting there can use this.
 */
const CURRENCY_WORDS = ['pound', 'dollar', 'shilling', 'rand', 'rupee', 'naira'];

/** Long enough to read the whole line, short enough to see it change. */
const CURRENCY_INTERVAL_MS = 2400;

/**
 * One word at a time, in a space wide enough for the longest of them.
 *
 * Two things this deliberately does not do.
 *
 * It does not stack all six in the DOM and fade between them, which is the
 * usual way. `scripts/prerender.mjs` writes these pages out as real HTML by
 * capturing the rendered document, so every hidden word would be baked into
 * the crawled `<h1>` — "Every room, tenant and pound dollar shilling rand
 * rupee naira, accounted for" is what a search engine would have read.
 *
 * And it does not let the line reflow on every tick. The heading is centred,
 * so a word that changes width shifts the whole line under the reader's eye.
 * The width is measured once with the element's own font and held, which
 * needs no second copy of the text to do it.
 *
 * Motion is handled by the opacity transition alone, so the reduced-motion
 * rule in index.css collapses it to a plain swap without stopping the words
 * changing — somebody who asked for less movement still learns the product
 * is sold in their currency.
 */
function CurrencyWord() {
  const ref = useRef<HTMLSpanElement>(null);
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(true);
  const [minWidth, setMinWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const font = window.getComputedStyle(el).font;
    if (!font) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.font = font;
    const widest = CURRENCY_WORDS.reduce((max, word) => Math.max(max, ctx.measureText(word).width), 0);
    // A hair over, so a rounding difference between measuring and painting
    // cannot clip the descender of a "g".
    if (widest > 0) setMinWidth(Math.ceil(widest) + 1);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      // Faded out, swapped, faded back in — so the word never changes mid-fade.
      setShown(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % CURRENCY_WORDS.length);
        setShown(true);
      }, 200);
    }, CURRENCY_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <span
      ref={ref}
      className={`text-brand inline-block text-left transition-opacity duration-200 ${
        shown ? 'opacity-100' : 'opacity-0'
      }`}
      style={minWidth ? { minWidth } : undefined}
    >
      {CURRENCY_WORDS[index]}
    </span>
  );
}

const FEATURES = [
  { icon: Building2, title: 'Properties & rooms', body: 'Per-room or whole-property lets, with facilities, utilities and rent on every unit.' },
  { icon: BadgeCheck, title: 'Verified payments', body: 'Tenants submit proof; nothing counts against a balance until a manager confirms it.' },
  { icon: Users, title: 'Tenant records', body: 'Contacts, ID, contracts, payment history and every extension you have granted.' },
  { icon: Zap, title: 'Property bills', body: 'Electricity, water, gas and wifi tracked per property, then split back to tenants.' },
  { icon: FolderOpen, title: 'Documents', body: 'Photos, contracts, IDs and receipts, stored securely and shared only when you choose.' },
  { icon: MessageSquare, title: 'SMS you control', body: 'Reminders are queued for review and sent when you press send — never on a timer.' },
];

export default function Landing() {
  const { data: pub } = useApi<PublicConfig>('/public/config');
  const canSignUp = pub?.signup_enabled !== false;

  return (
    <div className="min-h-screen bg-bg">
      <header className="flex items-center justify-between px-5 sm:px-8 h-16 max-w-7xl mx-auto">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="font-extrabold tracking-tight text-lg">PROVIA</span>
        </div>
        {/*
          Pricing belongs here, not only on the pricing page — a visitor
          deciding whether to bother needs it before they have gone anywhere.
        */}
        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/tour" className="hidden sm:block">
            <Button variant="ghost" size="sm">See it</Button>
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" size="sm">Pricing</Button>
          </Link>
          <Link to="/tenant-login" className="hidden sm:block">
            <Button variant="ghost" size="sm">Tenant portal</Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" size="sm">Sign in</Button>
          </Link>
          {canSignUp && (
            <Link to="/setup">
              <Button variant="primary" size="sm">Start free</Button>
            </Link>
          )}
        </div>
      </header>

      <section className="mesh px-5 sm:px-8 pt-16 pb-24 border-b border-line">
        <div className="max-w-4xl mx-auto text-center">
          <p className="label mb-5">Property management</p>
          <h1 className="text-display">
            Every room, tenant and <CurrencyWord />, accounted for.
          </h1>
          <p className="text-lg text-muted mt-6 max-w-2xl mx-auto leading-relaxed">
            Track properties and rooms, verify rent as it comes in, keep bills and documents
            in one place, and give tenants a portal of their own.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-9">
            {canSignUp ? (
              <Link to="/setup">
                <Button variant="primary" size="lg" icon={<ArrowRight className="h-4 w-4" />}>
                  Start free trial
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="primary" size="lg" icon={<ArrowRight className="h-4 w-4" />}>
                  Manager sign in
                </Button>
              </Link>
            )}
            <DemoButton demo={pub?.demo ?? null} />
          </div>

          {canSignUp && (
            <p className="text-sm text-muted mt-4">
              {pub?.trial_days ?? 7} days free · no card needed ·{' '}
              <Link to="/pricing" className="text-brand hover:underline">see pricing</Link>
            </p>
          )}
        </div>

        {/*
          The product itself, immediately.

          A list of features tells somebody what it does; a picture of the
          screen tells them whether they would want to use it, which is the
          question they are actually asking. These are real captures of the
          running app (see scripts/screenshots.mjs), so this section cannot
          quietly start advertising a version that no longer exists.
        */}
        <div className="max-w-5xl mx-auto mt-16">
          <ProductTour ids={['dashboard', 'payments', 'sms', 'reminders']} />
          <p className="text-center mt-6">
            <Link to="/tour" className="text-sm text-brand hover:underline">
              See every screen, including the tenant's →
            </Link>
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="panel">
              <div className="h-10 w-10 rounded-xl bg-brand/12 text-brand flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-bold tracking-tight">{f.title}</h3>
              <p className="text-sm text-muted mt-1.5 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/*
        Moving in.

        The objection that stops most people is not price, it is the forty
        tenancies already sitting in a spreadsheet. Saying plainly that they
        come across — and that they can be taken out again just as easily — is
        worth more than another feature tile.
      */}
      <section className="px-5 sm:px-8 pb-20 max-w-6xl mx-auto">
        <div className="panel sm:p-8">
          <div className="max-w-3xl">
            <h2 className="text-title">Moving to PROVIA</h2>
            <p className="text-muted mt-3 leading-relaxed">
              You almost certainly have all of this somewhere already — a spreadsheet, a
              folder of agreements, software you have outgrown. Bring it with you.
            </p>

            <div className="grid sm:grid-cols-3 gap-5 mt-8">
              <div>
                <p className="font-semibold">1. Export what you have</p>
                <p className="text-sm text-muted mt-1.5 leading-relaxed">
                  Any spreadsheet saved as CSV. Your column names do not have to match
                  ours — you tell us which is which, and you can see the match before
                  anything is saved.
                </p>
              </div>
              <div>
                <p className="font-semibold">2. Bring it in</p>
                <p className="text-sm text-muted mt-1.5 leading-relaxed">
                  Properties, rooms, tenants and tenancies. PROVIA checks the whole file
                  and tells you row by row what will happen before it writes anything.
                  Nobody is texted or emailed because you imported them.
                </p>
              </div>
              <div>
                <p className="font-semibold">3. Leave whenever you like</p>
                <p className="text-sm text-muted mt-1.5 leading-relaxed">
                  Everything exports again as a spreadsheet, a PDF or a data file, any
                  time, on any plan — including one that has lapsed. Your records are
                  yours.
                </p>
              </div>
            </div>

            <p className="text-sm text-faint mt-8 leading-relaxed">
              One thing PROVIA deliberately will not import is a rent ledger. A history
              of who paid what, assembled from another system's export with no way to
              check it, is how a tenant ends up chased for rent they already paid. Keep
              the old records; start the ledger here.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-line px-5 sm:px-8 py-10">
        <div className="max-w-6xl mx-auto space-y-5">
          <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <Link to="/tour" className="text-muted hover:text-ink transition-colors">See it</Link>
            <Link to="/pricing" className="text-muted hover:text-ink transition-colors">Pricing</Link>
            <Link to="/terms" className="text-muted hover:text-ink transition-colors">Terms</Link>
            <Link to="/privacy" className="text-muted hover:text-ink transition-colors">Privacy</Link>
            <Link to="/data-processing" className="text-muted hover:text-ink transition-colors">Data processing</Link>
            <Link to="/refunds" className="text-muted hover:text-ink transition-colors">Refunds</Link>
            <Link to="/cookies" className="text-muted hover:text-ink transition-colors">Cookies</Link>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="text-sm text-faint space-y-1">
              <p>
                <span className="text-muted font-medium">PROVIA</span> is built and delivered
                by <span className="text-muted font-medium">OPSERION</span>.
              </p>
              <p>© {new Date().getFullYear()} OPSERION. All rights reserved.</p>
            </div>
            <div className="flex gap-5 text-sm text-faint">
              <Link to="/pricing" className="hover:text-ink">Pricing</Link>
              <Link to="/login" className="hover:text-ink">Manager</Link>
              <Link to="/tenant-login" className="hover:text-ink">Tenant portal</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
